// Independent auth context for the Speaking Partner - Live feature.
// Deliberately NOT mounted globally (see AuthContext.tsx for that pattern) —
// only mounted inside SpeakingPartnerLayout, scoped to /speaking-partner/*,
// so anonymous visitors elsewhere on the site never pay for this extra fetch.
import React, { createContext, useContext, useEffect, useState } from "react";

export type PracticeLevel = "A1" | "A2" | "B1" | "B2";
export type PracticeVerificationStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface PracticeUserType {
  id: number;
  email: string;
  displayName: string;
  nativeLanguage: string;
  learningLanguage: string;
  level: PracticeLevel;
  available: boolean;
  verificationStatus: PracticeVerificationStatus;
  rejectionReason: string | null;
  consentAccepted: boolean;
}

interface PracticeAuthContextType {
  isAuthenticated: boolean;
  practiceUser: PracticeUserType | null;
  login: (email: string, password: string) => Promise<void>;
  register: (formData: FormData) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const PracticeAuthContext = createContext<PracticeAuthContextType | undefined>(undefined);

const API = process.env.REACT_APP_API_BASE_URL;

export const PracticeAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [practiceUser, setPracticeUser] = useState<PracticeUserType | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = async () => {
    try {
      const res = await fetch(`${API}/api/practice/auth/me`, { credentials: "include", cache: "no-store" });

      if (res.ok) {
        const data: PracticeUserType = await res.json();
        setPracticeUser(data);
        setIsAuthenticated(true);
      } else {
        setPracticeUser(null);
        setIsAuthenticated(false);
      }
    } catch {
      setPracticeUser(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    fetchMe().finally(() => setLoading(false));
  }, []);

  // Keeps lastActiveAt fresh so admin can see who's actually online right now.
  useEffect(() => {
    if (!isAuthenticated) return;
    const beat = () =>
      fetch(`${API}/api/practice/profile/me/heartbeat`, { method: "PUT", credentials: "include" }).catch(() => {});
    beat();
    const interval = setInterval(beat, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const login = async (email: string, password: string) => {
    let res: Response;
    try {
      res = await fetch(`${API}/api/practice/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
    } catch {
      throw new Error("Couldn't reach the server. Please check your connection and try again.");
    }

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || "Login failed");
    }

    const data: PracticeUserType = await res.json();
    setPracticeUser(data);
    setIsAuthenticated(true);
  };

  const register = async (formData: FormData) => {
    let res: Response;
    try {
      res = await fetch(`${API}/api/practice/auth/register`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
    } catch {
      // fetch() itself throws (not a 4xx/5xx response) on network drops, timeouts,
      // or an in-app browser killing the connection mid-upload — surface something
      // a user can act on instead of the raw "Failed to fetch" browser message.
      throw new Error(
        "Couldn't reach the server — this can happen on a slow connection while uploading your ID/selfie. Please check your connection and try again."
      );
    }

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || "Registration failed");
    }

    const data: PracticeUserType = await res.json();
    setPracticeUser(data);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/practice/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore — clear local state regardless
    }
    setPracticeUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <PracticeAuthContext.Provider
      value={{ isAuthenticated, practiceUser, login, register, logout, refresh: fetchMe }}
    >
      {children}
    </PracticeAuthContext.Provider>
  );
};

export const usePracticeAuth = () => {
  const context = useContext(PracticeAuthContext);
  if (!context) throw new Error("usePracticeAuth must be used within a PracticeAuthProvider");
  return context;
};
